import { Button, Checkbox, FormControl, FormControlLabel, TextField, Typography } from '@mui/material';
import { getAuth, signOut } from 'firebase/auth';
import { doc, getFirestore, serverTimestamp, writeBatch } from 'firebase/firestore';
import React, { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { getAuthErrorMessageForCode, handleError } from '../../../common/firebase/auth-error.service';
import { dutchPhoneNumber } from '../../../common/firebase/regex';
import { severity } from '../../../common/models/severity.enum';
import { ServiceManager } from '../../../services/service-manager';
import { roles, UserData } from '../models/user';
import styles from './NewUser.module.css';

interface NewUserProps {
    user: any,
    setUser(user: any): void,
    agreeEmailText: string,
    termsAndConditionsLink: string;
    privacyStatementLink: string;
}

const NewUser: FC<NewUserProps> = ({ user, setUser, agreeEmailText, termsAndConditionsLink, privacyStatementLink }) => {

    const [edit, setEdit] = useState<boolean>(false);

    const navigate = useNavigate();
    const { register, reset, handleSubmit, control, formState: { errors } } = useForm<UserData>();

    useEffect(() => {
        if (user.private && edit === false) {
            setEdit(true);
            reset({ public: user.public, private: user.private });
        }
    }, [])

    const onSubmit = (data: UserData) => {
        const auth = getAuth();
        data.private.roles = [roles.standard];
        data.private.email = user.email;
        data.private.userId = user.uid;
        if (data.private.agreeToReceiveEmailsWithNewsAndOffers === undefined) {
            data.private.agreeToReceiveEmailsWithNewsAndOffers = false;
        }
        data.private.lastEdit = serverTimestamp();
        data.public.lastEdit = serverTimestamp();
        data.public.userId = user.uid;

        const db = getFirestore();
        const batch = writeBatch(db);

        if (edit) {
            delete data.private.agreeToTermsAndConditionsAndPrivacy;
            delete data.private.roles;
            delete data.private.email;
        } else {
            const userRef = doc(db, `users/${user.email}`);
            batch.set(userRef, { userId: user.uid });
        }

        const privateRef = doc(db, `users/${user.email}/data/private`);
        if (edit) {
            batch.update(privateRef, data.private as any);
        } else {
            batch.set(privateRef, data.private);
        }
        const publicRef = doc(db, `users/${user.email}/data/public`);
        if (edit) {
            batch.update(publicRef, data.public as any);
        } else {
            batch.set(publicRef, data.public);
        }

        batch.commit().then(async () => {
            const token = await auth.currentUser.getIdTokenResult();
            const u = { ...user, private: data.private, public: data.public, token };
            setUser(u);
            const message = edit ? 'Uw gegevens zijn aangepast.' : `U bent geregistreerd. Welkom, ${data.private.name}!`;
            ServiceManager.snackbarService.openSnackbar(message, severity.success);
            setTimeout(() => {
                navigate('/app');
            }, 400)
        }).catch(err => {
            handleError(err);
        })
    }

    const logout = () => {
        const auth = getAuth();
        signOut(auth).then(() => {
            setUser(undefined);
            navigate('/auth/login');
        }).catch((error) => {
            ServiceManager.snackbarService.openSnackbar(getAuthErrorMessageForCode(error.code), severity.error);
        });
    }

    return (
        <div className={styles.NewUser} data-testid="NewUser">
            <form data-testid="new-user-form" onSubmit={(handleSubmit(onSubmit))}>
                {!edit &&
                    <>
                        Om u beter van dienst te kunnen zijn hebben we een aantal gegevens van u nodig.
                        Deze worden verwerkt in overeenstemming met ons privacybeleid.
                    </>}
                <div>
                    <TextField id="user-name"
                        inputProps={{
                            "data-testid": "user-name",
                        }}
                        fullWidth
                        className="form-input"
                        label="Voornaam"
                        {...register('private.name', {
                            required: { value: true, message: 'U moet uw voornaam invullen.' }, maxLength: {
                                value: 255,
                                message: 'Uw voornaam is te lang.'
                            },
                        })}
                        variant="standard"
                    />
                    {errors.private?.name && <p className="input-error">{errors.private?.name?.message}</p>}
                </div>
                <div>
                    <TextField id="user-surname"
                        inputProps={{
                            "data-testid": "user-surname",
                        }}
                        fullWidth
                        className="form-input"
                        label="Achternaam"
                        {...register('private.surname', {
                            required: { value: true, message: 'U moet uw achternaam invullen.' }, maxLength: {
                                value: 255,
                                message: 'Uw achternaam is te lang.'
                            },
                        })}
                        variant="standard"
                    />
                    {errors.private?.surname && <p className="input-error">{errors.private?.surname?.message}</p>}
                </div>
                <div>
                    <TextField id="user-addition"
                        inputProps={{
                            "data-testid": "user-addition",
                        }}
                        fullWidth
                        className="form-input"
                        label="Tussenvoegsel"
                        {...register('private.addition', {
                            maxLength: {
                                value: 255,
                                message: 'Uw tussenvoegsel is te lang.'
                            },
                        })}
                        helperText="Optioneel"
                        variant="standard"
                    />
                    {errors.private?.addition && <p className="input-error">{errors.private?.addition?.message}</p>}
                </div>
                <div>
                    <TextField id="user-public-name"
                        inputProps={{
                            "data-testid": "user-public-name",
                        }}
                        fullWidth
                        className="form-input"
                        label="Publieke Naam"
                        {...register('public.publicName', {
                            required: { value: true, message: 'U moet uw publieke naam invullen.' }, maxLength: {
                                value: 255,
                                message: 'Uw publieke naam is te lang.'
                            },
                        })}
                        helperText="Zichtbaar voor andere gebruikers"
                        variant="standard"
                    />
                    {errors.public?.publicName && <p className="input-error">{errors.public?.publicName?.message}</p>}
                </div>
                <div>
                    <TextField id="user-company"
                        inputProps={{
                            "data-testid": "user-company",
                        }}
                        fullWidth
                        className="form-input"
                        label="Organisatie / bedrijf"
                        {...register('private.company', {
                            required: { value: true, message: 'U moet uw organisatie naam invullen.' },
                            maxLength: {
                                value: 255,
                                message: 'Uw organisatie naam is te lang.'
                            },
                        })}
                        variant="standard"
                    />
                    {errors.private?.company && <p className="input-error">{errors.private?.company?.message}</p>}
                </div>
                <div>
                    <TextField id="user-phone"
                        inputProps={{
                            "data-testid": "user-phone",
                        }}
                        fullWidth
                        className="form-input"
                        label="Telefoonnummer"
                        {...register('private.phone', {
                            pattern: {
                                value: dutchPhoneNumber,
                                message: 'Uw telefoonnummer voldoet niet aan de regels.'
                            },
                            maxLength: {
                                value: 255,
                                message: 'Uw telefoonnummer is te lang.'
                            },
                        })}
                        placeholder="+31"
                        helperText="Optioneel"
                        variant="standard"
                    />
                    {errors.private?.phone && <p className="input-error">{errors.private?.phone?.message}</p>}
                </div>
                <div className={styles.actions}>
                    {!edit &&
                        <>
                            <FormControlLabel
                                label={<Typography variant="body2" color="textSecondary">Ik ga akkoord met <a target="_blank" rel="noreferrer" href={termsAndConditionsLink}>Gebruiksvoorwaarden</a> en <a target="_blank" rel="noreferrer" href={privacyStatementLink}>Privacy Policy</a></Typography>}
                                control={
                                    <Checkbox
                                        data-testid="user-agree-toc"
                                        {...register('private.agreeToTermsAndConditionsAndPrivacy', {
                                            required: { value: true, message: 'U moet onze algemene gebruiksvoorwaarden en privacy statement accepteren.' },
                                        })}
                                        indeterminate={false}
                                    />
                                }
                            />
                            {errors.private?.agreeToTermsAndConditionsAndPrivacy && <p className="input-error">{errors.private?.agreeToTermsAndConditionsAndPrivacy?.message}</p>}
                        </>
                    }
                    <FormControlLabel
                        label={<Typography variant="body2" color="textSecondary">{agreeEmailText}</Typography>}
                        control={
                            <Controller
                                name="private.agreeToReceiveEmailsWithNewsAndOffers"
                                control={control}
                                render={({ field }) => {
                                    return <Checkbox
                                        {...field}
                                        data-testid="user-agree-email"
                                        checked={field['value'] ?? false}
                                        indeterminate={false}
                                    />
                                }}
                            />
                        }
                    />
                    {edit ?
                        <div className={styles.actions}>
                            <Button color="primary" data-testid="edit-submit" size="small" variant="contained" type="submit">Gegevens aanpassen</Button>
                        </div> :
                        <div className={styles.actions}>
                            <Button color="primary" data-testid="register" size="small" variant="contained" type="submit">Registreren</Button>
                            <Button color="secondary" data-testid="logout" size="small" variant="outlined" onClick={logout}>Logout</Button>
                        </div>
                    }
                </div>
            </form>
        </div>
    )
};

export default NewUser;
